// src/screens/s3/ProgrammeSummary.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Session 3 — Programme Summary
// 6 metric cards in a 2x3 grid showing the impact of the plan
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { ClientData } from '../../types/ClientData';
import { downloadProgrammeSummary } from '../../utils/downloadPDF';
import { formatCurrency } from '../../utils/format';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

export default function ProgrammeSummary({ data, onUpdate }: Props) {
  // Calculate metrics
  const doNothingAge = data.voyantDoNothing?.retirementAge || 0;
  const finalFreedomAge = data.s3FinalFreedomAge || data.targetFreedomAge || 0;
  const rawShift = doNothingAge - finalFreedomAge;
  const freedomAgeShift = rawShift > 0 && doNothingAge > 0 && finalFreedomAge > 0 ? rawShift : null;
  const monthlyPersonal = (data.pensionMonthly || 0) + (data.isaContribution || 0) + (data.mortgageOP || 0);
  const monthlyBusiness = 0; // TODO: read s2PensionMonthly when S2 engine is wired here
  const ctSaved = 0;         // TODO: derive from s2PensionMonthly × effectiveCTRate
  const totalPensionContributions = (data.pensionMonthly || 0) * 12 * ((data.targetFreedomAge || 0) - (data.clientAge || 0));
  const stressResult = `${data.s3FinalFundedToAge || 0} years ${data.s3FinalStressResult === 'funded' ? '✓' : data.s3FinalStressResult === 'shortfall' ? '⚠' : ''}`;

  interface MetricCard {
    title: string;
    value: string | number;
    unit?: string;
    note?: string;
    highlight?: boolean;
  }

  const metrics: MetricCard[] = [
    {
      title: 'Freedom Age Shift',
      value: freedomAgeShift !== null ? freedomAgeShift : '—',
      unit: freedomAgeShift !== null ? 'years earlier' : undefined,
      highlight: freedomAgeShift !== null && freedomAgeShift > 0,
    },
    {
      title: 'Monthly Contribution (Personal)',
      value: formatCurrency(monthlyPersonal),
      note: 'Pension + ISA + Mortgage OP',
    },
    {
      title: 'Monthly Contribution (Business)',
      value: formatCurrency(monthlyBusiness),
      note: monthlyBusiness > 0 ? 'Employer pension + business reserves' : 'Awaiting S2 data',
    },
    {
      title: 'Tax Saved (CT Efficiency)',
      value: formatCurrency(ctSaved),
      note: ctSaved > 0 ? 'Annual CT relief on employer pension' : 'Awaiting S2 data',
    },
    {
      title: 'Total Pension Contributions',
      value: formatCurrency(totalPensionContributions),
      note: `£${data.pensionMonthly || 0}/month × 12 × ${(data.targetFreedomAge || 0) - (data.clientAge || 0)} years`,
    },
    {
      title: 'Plan Resilience',
      value: stressResult,
      note: 'Stress tested at 4% growth (half base rate)',
      highlight: data.s3FinalStressResult === 'funded',
    },
  ];

  const handleDownload = async () => {
    await downloadProgrammeSummary(data);
  };

  return (
    <div className="max-w-5xl mx-auto py-8 px-6">
      <h2 className="text-xl font-bold text-ma-navy mb-2">Programme Summary</h2>
      <p className="text-gray-600 mb-8">
        Six key metrics showing the impact of your three-session programme.
      </p>

      {/* 2x3 Grid of Metric Cards */}
      <div className="grid grid-cols-2 gap-6 mb-8">
        {metrics.map((metric, idx) => (
          <div
            key={idx}
            className={`rounded-2xl border-2 p-6 ${
              metric.highlight
                ? 'bg-green-50 border-green-200'
                : 'bg-white border-gray-200'
            }`}
          >
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              {metric.title}
            </div>
            <div className={`text-3xl font-bold ${metric.highlight ? 'text-green-700' : 'text-ma-navy'}`}>
              {metric.value}
            </div>
            {metric.unit && (
              <p className="text-sm text-gray-600 mt-1">{metric.unit}</p>
            )}
            {metric.note && (
              <p className="text-xs text-gray-500 mt-2">{metric.note}</p>
            )}
          </div>
        ))}
      </div>

      {/* Download Button */}
      <div className="flex items-center justify-center pt-6 border-t border-gray-200">
        <button
          onClick={handleDownload}
          className="px-6 py-3 bg-ma-navy text-white rounded-lg text-sm font-semibold hover:bg-opacity-90 transition"
        >
          ⬇ Download Summary
        </button>
      </div>
    </div>
  );
}
